// Ocean for the "Sinking Ship" map.
//
// Wraps three's `WaterMesh` addon (the WebGPU TSL ocean from the
// `webgpu_ocean` example) and customises it with two extras the swim
// mechanic depends on:
//
//   1. **Geometric waves.** The plane is subdivided so we can override
//      `material.positionNode` with a Gerstner-style vertical
//      displacement (sum of three sines at world XZ). Displacement
//      amplitude fades to zero with distance from the camera so the
//      horizon stays flat (no Nyquist aliasing on the coarse grid out
//      there) while the patch right around the player visibly bobs.
//      The silhouette of the displaced edge is the natural waterline
//      seen when the camera is at / under the surface — replaces the
//      previous screen-space wavy split path.
//
//   2. **Forward-clip discard.** `material.opacityNode` multiplies the
//      base alpha by `step(clipOffset, dot(worldPos - camPos,
//      camForward))`, so the mesh is rendered only forward of the
//      camera (skipping a small `clipOffset` buffer right in front).
//      Combined with double-sided rendering, this means: when the
//      camera is at the surface, you see the water mesh starting just
//      in front of you and extending forward; underwater, you see the
//      same mesh from below, again only forward. The visible mesh
//      edge is the natural horizon / waterline.
//
// `cameraForward` is updated each render frame via `stepRender` from
// `ctx.camera()`. Sim-side init is a no-op — water has no collision in
// this map.
//
// Reference: node_modules/three/examples/jsm/objects/WaterMesh.js
//            three.js example: webgpu_ocean.html

import * as THREE from 'three';
import type { NodeMaterial } from 'three/webgpu';
import { WaterMesh } from 'three/examples/jsm/objects/WaterMesh.js';
import {
  Fn, vec2, vec3, float, sin, dot, step, smoothstep, length, select, uniform,
  positionLocal, positionWorld, cameraPosition, frontFacing
} from 'three/tsl';
import { loadKtx2 } from '../../../../render/textures/ktx2Loader';
import { CONFIG } from '../../../../config';
import type { Behavior, MapContext } from '../../../Behavior';

/** Behaviors that drive a water surface with vertical wave displacement
 *  expose this so sim-side systems (character buoyancy / submersion) can
 *  query the wave height under a point. Returns the vertical displacement
 *  in metres ABOVE the behavior's base water plane — World composes that
 *  with the map's `properties.waterY` to get an absolute world-Y. */
export interface WaterWaveProvider {
  sampleWaveOffset(x: number, z: number): number;
}

export interface OceanWaterParams {
  /** Plane size on each side, in metres. The default 10000m matches the
   *  three.js example so the horizon reads as "open ocean to the eye"
   *  without a visible edge. */
  size: number;
  /** Plane segments per side. Higher = more verts so geometric waves
   *  show through near the camera; lower = cheaper but flatter. The
   *  cell size is `size / segments`; wave wavelengths must be >= 2 *
   *  cell to render without aliasing. */
  segments: number;
  /** World Y of the water surface. Sits below the boat's surfaceY so
   *  the hull's draft sits underwater naturally. */
  y: number;
  /** Sun direction (TOWARD the sun). Should agree with the directional
   *  light in map.json + the SeaSky material so reflection / specular
   *  highlights land in the right place. */
  sunDir: [number, number, number];
  /** Sun colour for the specular term. */
  sunColor: string;
  /** Deep water tint. The mesh blends this with the reflection by
   *  Schlick fresnel; lower-brightness colours read as deeper sea. */
  waterColor: string;
  /** Higher = more pronounced normal-map distortion / wave detail. */
  distortionScale: number;
  /** Tiles the normal map. Lower = larger waves; higher = busier. */
  size_uv: number;
  /** Reflector resolution scale. 0.5 = half-res mirror buffer (cheap +
   *  hides aliasing). 1 = full-res. */
  resolutionScale: number;
}

// Wave uniforms are MODULE-SCOPED + EXPORTED so the underwater post pass
// (PostPipeline.ts) can re-use the exact same uniform instances when
// computing the analytic wave height for its mask threshold. Sharing
// uniforms — rather than each shader reading `performance.now()` and
// `CONFIG` independently — guarantees both the visible mesh
// displacement and the post-pass mask see byte-identical values, so
// the boundary stays glued to the wave silhouette regardless of the
// frame's call ordering or float precision.
export const oceanWaveTime    = uniform(0);
export const oceanWaveAmp     = uniform(CONFIG.render.underwater.waveAmp);
export const oceanWaveFalloff = uniform(CONFIG.render.underwater.waveFalloffMeters);
// Plane cell size = `params.size / params.segments`. Exported so the
// post-pass mask can bilerp the wave at the same vertex grid the mesh
// samples — a continuous analytic eval there diverges from the visible
// mesh whenever the mesh undersamples (waves under ~4× cell size). Init
// is a sane default; init() overwrites with the actual map params.
export const oceanCellSize    = uniform(10000 / 1024);

export default function createOceanWater(): Behavior & WaterWaveProvider {
  let waterMesh: WaterMesh | null = null;
  let normalsTex: THREE.Texture | null = null;
  let params: OceanWaterParams | null = null;
  let getCamera: (() => THREE.Camera | null) | null = null;
  // Authoritative sim-time the wave field is sampled at. Set in `stepSim`
  // from the value World.step passes through (`world.tick / TICK_HZ`),
  // identical on server and every client. `sampleWaveOffset` and the
  // `uWaveTime` GPU uniform both read from here so server-side buoyancy,
  // client-side buoyancy, and the visible wave on screen all agree on
  // crest position at any given simulated moment. Was previously
  // `performance.now()` per peer, which drifted across machines and
  // caused per-client divergent submersion (and so swim-mode parity
  // bugs). Stays at 0 until the first stepSim, so the very first render
  // frame after init renders the t=0 wave field rather than NaN.
  let simTimeSec = 0;

  // Camera-forward uniform fed to the forward-clip in the fragment
  // shader. Updated each render frame from the active camera's world
  // direction. Init to +Z so the first frame (before stepRender runs)
  // doesn't render the whole mesh as clipped.
  const uCameraForward = uniform(new THREE.Vector3(0, 0, 1));
  const uClipOffset = uniform(CONFIG.render.underwater.clipOffset);
  // Aliases so the rest of the file reads naturally; shared with post.
  const uWaveTime = oceanWaveTime;
  const uWaveAmp = oceanWaveAmp;
  const uWaveFalloff = oceanWaveFalloff;

  function readParams(raw: Record<string, unknown>): OceanWaterParams {
    return {
      size: (raw.size as number) ?? 10000,
      segments: (raw.segments as number) ?? 1024,
      y: (raw.y as number) ?? -0.6,
      sunDir: (raw.sunDir as [number, number, number]) ?? [-0.42, 0.78, 0.46],
      sunColor: (raw.sunColor as string) ?? '#ffffff',
      waterColor: (raw.waterColor as string) ?? '#001e0f',
      distortionScale: (raw.distortionScale as number) ?? 3.7,
      size_uv: (raw.size_uv as number) ?? 1.0,
      resolutionScale: (raw.resolutionScale as number) ?? 0.5
    };
  }

  return {
    /** Analytic wave height above the base water plane at world (x, z).
     *  Mirrors the GPU shader's three-sine sum — but with `fade = 1`
     *  (no camera-distance falloff) because sim has no camera and players
     *  always live well within the visible-wave radius around their own
     *  camera.
     *
     *  Time source is `simTimeSec` (authoritative tick time), shared with
     *  the render-side `uWaveTime` uniform a few lines below — both see
     *  the same value at the same simulated moment, so a server-side
     *  trough is a client-side trough is a visible-on-screen trough.
     *  Critical for swim-mode parity: buoyancy reads this and decides
     *  `inWater`, which feeds replicated `isSwimming`. */
    sampleWaveOffset(x: number, z: number): number {
      const amp = CONFIG.render.underwater.waveAmp;
      if (amp === 0) return 0;
      const t = simTimeSec;
      const w1 = Math.sin(x * 0.18 + z * 0.07 + t * 0.55);
      const w2 = Math.sin(x * -0.09 + z * 0.21 + t * 0.78);
      const w3 = Math.sin(x * 0.27 + z * -0.15 + t * 0.42);
      return (w1 * 0.55 + w2 * 0.30 + w3 * 0.15) * amp;
    },

    /** Captures the authoritative sim-time so the next `sampleWaveOffset`
     *  call (from buoyancy) and the next `stepRender` (uniform pump) both
     *  consume an identical value. No other per-tick work — the actual
     *  wave-field math is pure and stateless. */
    stepSim(_dt: number, _suppressDamage: boolean, simTimeSecArg: number): void {
      simTimeSec = simTimeSecArg;
    },

    init(ctx: MapContext, raw: Record<string, unknown>) {
      if (!params) params = readParams(raw);
      // Push the plane's cell size into the shared uniform so the post-pass
      // bilerp aligns with this map's actual vertex grid (not the default).
      oceanCellSize.value = params.size / params.segments;
      if (ctx.layer !== 'render') return;

      getCamera = () => ctx.camera();
      const tex = loadKtx2('/materials/water/waternormals.ktx2');
      tex.wrapS = THREE.RepeatWrapping;
      tex.wrapT = THREE.RepeatWrapping;
      normalsTex = tex;

      const sunDir = new THREE.Vector3(...params.sunDir).normalize();
      // Subdivided plane — Gerstner vertex displacement only shows when
      // there's enough verts in the falloff zone. With 512 segments on
      // a 10000m plane each cell is ~20m, so a 40m+ wavelength resolves
      // cleanly. Bump segments higher (with proportional perf cost) for
      // tighter wavelengths, or shrink `size` if the open-ocean horizon
      // isn't needed.
      const geometry = new THREE.PlaneGeometry(
        params.size, params.size, params.segments, params.segments
      );
      const mesh = new WaterMesh(geometry, {
        waterNormals: tex,
        sunDirection: sunDir,
        sunColor: new THREE.Color(params.sunColor),
        waterColor: new THREE.Color(params.waterColor),
        distortionScale: params.distortionScale,
        resolutionScale: params.resolutionScale
      });
      mesh.rotation.x = -Math.PI / 2;
      mesh.position.y = params.y;
      mesh.size.value = params.size_uv;

      mesh.castShadow = false;
      mesh.receiveShadow = false;

      // Material customisation. The WaterMesh's NodeMaterial owns the
      // reflector, normal-map distortion, and Schlick fresnel — we leave
      // those alone and only override:
      //
      //   • positionNode  — Gerstner vertex displacement on a local-XY
      //                     plane (which maps to world XZ after the
      //                     -π/2 X rotation; world up is local +Z).
      //   • opacityNode   — multiply existing alpha by a forward-clip
      //                     mask so the mesh renders only ahead of the
      //                     camera.
      //   • side          — DoubleSide so the underside is visible when
      //                     the camera dips below the surface.
      const mat = mesh.material as NodeMaterial;
      mat.side = THREE.DoubleSide;
      // alphaTest > 0 is what actually turns the alpha=0 clip into a
      // hard discard; without it the mesh renders translucent fragments
      // on the clipped side, which would z-fight with anything beyond.
      mat.alphaTest = 0.5;
      // The WaterMesh addon sets `transparent = true` for blending, but
      // we don't actually rely on alpha blending — the forward-clip is
      // a hard alphaTest discard, and the surface itself is fully
      // opaque. Forcing it back into the opaque pass guarantees the
      // wave-displaced fragments write to the shared depth buffer that
      // the underwater post pass samples for its world-Y mask. With
      // transparent=true, the mask was reading un-displaced depth (or
      // missing the water mesh entirely in some configurations) and the
      // mask boundary collapsed to a flat horizon line.
      mat.transparent = false;
      mat.depthWrite = true;
      mat.needsUpdate = true;

      // ── Vertex displacement. Plane is in local XY (z=0); after the
      // mesh.rotation.x = -π/2, local Y → world -Z and local Z → world
      // +Y. So world XZ = (localX, -localY) and a vertical lift in
      // world space = a +localZ delta.
      mat.positionNode = Fn(() => {
        const worldXZ = vec2(positionLocal.x, positionLocal.y.negate());
        const dx = worldXZ.x.sub(cameraPosition.x);
        const dz = worldXZ.y.sub(cameraPosition.z);
        const distFromCam = length(vec2(dx, dz));
        // Smoothstep from 1 (at the camera) to 0 (at falloff radius).
        // Keeps the horizon flat / non-aliased; lets only the local
        // patch around the player actually displace.
        const fade = smoothstep(uWaveFalloff, float(0), distFromCam);
        // Three sines summed at hand-picked wavelengths. World-XZ
        // sampled (not local) so the wave field is stationary as the
        // mesh might shift; per-component direction vectors keep
        // crests off-axis so the surface doesn't read as a single
        // sinusoidal sweep.
        const t = uWaveTime;
        const w1 = sin(dot(worldXZ, vec2(0.18, 0.07)).add(t.mul(0.55)));
        const w2 = sin(dot(worldXZ, vec2(-0.09, 0.21)).add(t.mul(0.78)));
        const w3 = sin(dot(worldXZ, vec2(0.27, -0.15)).add(t.mul(0.42)));
        const height = w1.mul(0.55).add(w2.mul(0.30)).add(w3.mul(0.15))
          .mul(uWaveAmp).mul(fade);
        // Local-Z is the world-up axis after the -π/2 X rotation.
        return positionLocal.add(vec3(float(0), float(0), height));
      })();

      // ── Forward-clip mask. Drop fragments whose world position projects
      // to less than `clipOffset` metres ahead of the camera along the
      // camera's forward axis. Combined with alphaTest above this is a
      // hard discard.
      const forwardDot = positionWorld.sub(cameraPosition).dot(uCameraForward);
      const clipMask = step(uClipOffset, forwardDot);
      // The WaterMesh ships with `opacityNode = alpha` (default 1.0); we
      // overwrite with the clip mask. step() returns 0 (clipped) or 1
      // (visible), and `alphaTest = 0.5` above turns the 0 into a hard
      // discard, so multiplying by `alpha` would be a no-op anyway.
      mat.opacityNode = clipMask;

      // ── Back-face shading. The WaterMesh's existing colorNode samples
      // a reflector configured for the top side of the plane. With
      // DoubleSide, looking up from underwater would otherwise paint the
      // sky reflection on the underside — clearly wrong. Mix in a deep-
      // water tint when the fragment is on the back face so the
      // underside reads as dark, lit-from-above water.
      const originalColor = mat.colorNode;
      if (originalColor) {
        const deepUnderside = vec3(0.018, 0.075, 0.115);
        mat.colorNode = select(
          frontFacing,
          originalColor as unknown as Parameters<typeof select>[1],
          deepUnderside
        ) as typeof mat.colorNode;
      }

      waterMesh = mesh;

      if (ctx.mapGroup) ctx.mapGroup.add(mesh);
      ctx.registerObject3D('ocean', mesh);
    },

    stepRender() {
      // Pump time + camera-forward each frame so the geometric waves
      // animate and the forward-clip tracks where the player's looking.
      // Time is the authoritative `simTimeSec` set in stepSim — at 60 Hz
      // the wave frequencies (max ω ≈ 0.78) advance only ≈ 0.013 rad per
      // tick, which reads as smooth visual motion without any wall-clock
      // smoothing. Wall-clock smoothing was the bug: each peer's clock
      // drifted independently and the wave field desynced across clients,
      // breaking buoyancy parity (sampleWaveOffset reads the same field).
      // Trade-off: waves freeze along with the sim during round-over, but
      // that freeze lasts ROUND_RESET_DELAY_MS (~4 s) and is consistent
      // across peers — preferable to per-client divergent waves.
      uWaveTime.value = simTimeSec;
      const cam = getCamera?.();
      if (cam) {
        const f = uCameraForward.value;
        cam.getWorldDirection(f);
      }
      // Live config push so debug edits to amp / falloff / clip take
      // effect without a map reload.
      uWaveAmp.value = CONFIG.render.underwater.waveAmp;
      uWaveFalloff.value = CONFIG.render.underwater.waveFalloffMeters;
      uClipOffset.value = CONFIG.render.underwater.clipOffset;
    },

    dispose() {
      if (waterMesh) {
        waterMesh.geometry.dispose();
        const mat = waterMesh.material as THREE.Material | THREE.Material[];
        if (Array.isArray(mat)) {
          for (const m of mat) m.dispose();
        } else {
          mat.dispose();
        }
        waterMesh = null;
      }
      if (normalsTex) {
        normalsTex.dispose();
        normalsTex = null;
      }
    }
  };
}
